import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { fullFormats } from 'ajv-formats/dist/formats';

import { useDeepCompareMemo, useDeepCompareEffect, captureError } from '@clatter/platform';
import { addNotification } from '../../../../store';
import { buildThumbnailUrl, mergeSchemas, checkSchemaReady } from '../../../../helpers';
import { useTabbedForm, useSlideThumbnailPolling } from '../../../../hooks';
import {
  useGetProjectTemplateByIdQuery,
  useUpdateProjectMutation,
  useUpdateDocumentMutation,
} from '../../../../services';
import { useWorkspace } from '../../../../context/WorkspaceProvider';
import {
  NgCard,
  NgCardContent,
  NgDivider,
  SchemaForm,
  NgPanelExpandButton,
  SlideStatusDot,
  StickyHeader,
} from '../../../UI';
import { PlacematContext } from './PlacematContext';
import { PlacematLayoutPreview } from './PlacematLayoutPreview';
import { GenerateDocumentsSection } from '../components/GenerateDocumentsSection';

const STICKY_TOP = 65;
const LEFT_RAIL_WIDTH = 320;
const LEFT_RAIL_WIDTH_EXPANDED = 480;
const RIGHT_RAIL_WIDTH = 260;

const StyledLayout = styled.div<{ $leftExpanded: boolean }>`
  display: grid;
  grid-template-columns: ${({ $leftExpanded }) => ($leftExpanded ? LEFT_RAIL_WIDTH_EXPANDED : LEFT_RAIL_WIDTH)}px 1fr ${RIGHT_RAIL_WIDTH}px;
  gap: ${({ theme }) => theme.spacing.medium};
  align-items: start;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr;
  }
`;

const StyledCardWrapper = styled.div`
  position: relative;
`;

const StyledLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`;

const StyledPageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: ${({ $active, theme }) => ($active ? theme.palette.linkBackground : 'transparent')};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.main};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  color: ${({ $active, theme }) => ($active ? theme.palette.primaryButton : theme.palette.textMedium)};
  text-align: left;
  margin-bottom: 2px;

  &:hover {
    background: ${({ theme }) => theme.palette.iconButtonHoverBackground};
    color: ${({ theme }) => theme.palette.text};
  }
`;

const StyledStatusDot = styled.span<{ $ready: boolean }>`
  color: ${({ $ready, theme }) => ($ready ? theme.palette.success : theme.palette.grayLighter)};
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

interface PlacematGeneratorPageProps {
  projectData: any;
  isLoading: boolean;
  noTargetMessage?: string;
}

const EMPTY_LAYOUT = {
  templateSize: { width: 1, height: 1 },
  shapeVariables: [] as any[],
};

// fixed pages — later replaced by per-page entries from the backend (like presentation slides).
const PAGES = [
  { name: 'front', displayName: 'Front Side' },
  { name: 'back', displayName: 'Back Side' },
];

const SETTINGS_TAB = 'settings';

export const PlacematGeneratorPage: FC<PlacematGeneratorPageProps> = ({
  projectData,
  isLoading: isParentLoading,
  noTargetMessage = 'Click a box in the placemat layout to get started',
}) => {
  const dispatch = useDispatch();
  const { setLongPullItemId } = useWorkspace();

  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const [isLeftExpanded, setIsLeftExpanded] = useState(false);
  const handleLeftRailToggleExpand = useCallback(() => setIsLeftExpanded((prev) => !prev), []);
  const generateFormMethods = useForm();

  const { data: workspaceTemplateData, isLoading: isTemplateLoading } = useGetProjectTemplateByIdQuery(
    { workspaceId: projectData?.ProjectTemplate?.ID },
    { skip: !projectData?.ProjectTemplate?.ID },
  );

  const [updateDocument, { isLoading: isUpdateDocumentLoading }] = useUpdateDocumentMutation();
  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();

  const isLoading = isParentLoading || isTemplateLoading || isUpdateDocumentLoading || isUpdateProjectLoading;

  const pptxDocument = useMemo(
    () => projectData?.Documents?.find((doc: any) => doc.DocumentTemplate?.OutputType === 'pptx'),
    [projectData],
  );

  const { xLayout, thumbnailLocation, pptxValuesSchema } = useDeepCompareMemo(() => {
    const pptxTemplate = workspaceTemplateData?.DocumentTemplates?.find(
      (documentTemplate: any) => documentTemplate.OutputType === 'pptx',
    );
    const layout = (pptxTemplate?.ValuesSchema as any)?.['x-layout'] ?? EMPTY_LAYOUT;
    return {
      xLayout: layout,
      thumbnailLocation: pptxTemplate?.Asset?.CurrentVersion?.ThumbnailLocation ?? null,
      pptxValuesSchema: pptxTemplate?.ValuesSchema ?? null,
    };
  }, [workspaceTemplateData]);

  // root schema — has { front: {...}, back: {...}, ...settingsFields } at top level.
  const rootSchema = useDeepCompareMemo(
    () => mergeSchemas([pptxValuesSchema, workspaceTemplateData?.ValuesSchema]),
    [workspaceTemplateData, pptxValuesSchema],
  );

  const ajvSchema = useDeepCompareMemo(() => rootSchema ?? { type: 'object', properties: {} }, [rootSchema]);

  const settingsSchema = useDeepCompareMemo(() => {
    const schema = workspaceTemplateData?.ValuesSchema ?? { type: 'object', properties: {} };
    const properties = (schema.properties ?? {}) as Record<string, any>;
    const required = ((schema.required ?? []) as string[]).filter((key) => key in properties);
    return { ...schema, properties, required };
  }, [workspaceTemplateData]);

  const {
    activeTab: _activeTab,
    handleTabChange,
    onDirtyChange,
    onValidChange,
    activeTabIsDirty,
  } = useTabbedForm({
    tabs: [
      { name: SETTINGS_TAB, schema: settingsSchema, initialValues: {} },
      ...PAGES.map((page) => ({
        name: page.name,
        schema: (rootSchema?.properties as any)?.[page.name] ?? null,
        initialValues: {},
      })),
    ],
    onSave: async () => {
      await handleSave();
    },
    initialTab: PAGES[0].name,
    autoSave: true,
    getActiveValues: () => placematFormMethods.getValues(),
  });
  const activeTab = _activeTab ?? PAGES[0].name;

  // per-page schema for the active tab (only used when activeTab is a page, not 'settings').
  const activePageSchema = useDeepCompareMemo(
    () => (rootSchema?.properties as any)?.[activeTab] ?? { type: 'object', properties: {} },
    [rootSchema, activeTab],
  );

  const savedValues = useDeepCompareMemo(() => pptxDocument?.Values ?? {}, [pptxDocument]);

  const settingsKeys = useDeepCompareMemo(() => Object.keys(settingsSchema.properties ?? {}), [settingsSchema]);

  const initialValues = useDeepCompareMemo(() => {
    const settingsValues = Object.fromEntries(
      settingsKeys.filter((key: string) => key in savedValues).map((key: string) => [key, savedValues[key]]),
    );
    const pageValues = Object.fromEntries(PAGES.map((page) => [page.name, savedValues?.[page.name] ?? {}]));
    return { ...settingsValues, ...pageValues };
  }, [savedValues, settingsKeys]);

  const placematFormMethods = useForm<any>({
    mode: 'onChange',
    // @ts-ignore TS2589
    resolver: ajvResolver(ajvSchema as any, { formats: fullFormats, strict: false }),
  });

  const assetsRawFieldName = useDeepCompareMemo(() => {
    const firstPageProps = (rootSchema?.properties as any)?.[PAGES[0].name]?.properties;
    if (!firstPageProps) return null;
    return (
      Object.entries(firstPageProps).find(([, def]: [string, any]) => def?.inputType === 'assets-picker')?.[0] ?? null
    );
  }, [rootSchema]);

  const slotFieldName = assetsRawFieldName && activeTab !== SETTINGS_TAB ? `${activeTab}.${assetsRawFieldName}` : null;

  const formValues = placematFormMethods.watch();

  const slideNumber = activeTab === SETTINGS_TAB ? 1 : PAGES.findIndex((p) => p.name === activeTab) + 1;

  const slideThumbnailUrl = useMemo(() => {
    if (!thumbnailLocation || activeTab === SETTINGS_TAB) return null;
    const slideUrl = thumbnailLocation.replace(/-\d+(\/thumbnail)/, `-${slideNumber}$1`);
    const pageValues = (formValues as any)?.[activeTab] ?? {};

    const values: Record<string, any> = {};

    const expandFieldValue = (key: string, fieldValue: any) => {
      if (fieldValue == null) return;
      if (typeof fieldValue === 'object') {
        values[`${key}.label`] = fieldValue.label;
        values[`${key}.value`] = fieldValue.value;
      } else {
        values[key] = fieldValue;
      }
    };

    settingsKeys.forEach((settingsKey: string) => {
      expandFieldValue(settingsKey, (formValues as any)?.[settingsKey]);
    });

    Object.entries(pageValues).forEach(([fieldKey, fieldValue]) => {
      if (fieldKey === 'slots') return;
      expandFieldValue(`${activeTab}.${fieldKey}`, fieldValue);
    });

    return buildThumbnailUrl(slideUrl, null, values);
  }, [thumbnailLocation, slideNumber, activeTab, formValues, settingsKeys]);

  const { thumbnailUrl: slideThumbnailBlobUrl } = useSlideThumbnailPolling(slideThumbnailUrl, true);

  const isPageReady = useCallback(
    (pageName: string) => {
      const pageSchema = (rootSchema?.properties as any)?.[pageName];
      return checkSchemaReady(pageSchema, (formValues as any)?.[pageName] ?? {});
    },
    [rootSchema, formValues],
  );

  const pageReadiness = useMemo(
    () => Object.fromEntries(PAGES.map((page) => [page.name, isPageReady(page.name)])),
    [isPageReady],
  );

  const isSettingsReady = checkSchemaReady(settingsSchema, formValues);

  const isDocumentReady = isSettingsReady && PAGES.every((page) => pageReadiness[page.name]);

  const saveValues = async (data: any) => {
    if (!projectData || !pptxDocument) return;
    const pagePayload = Object.fromEntries(PAGES.map((page) => [page.name, data[page.name] ?? {}]));
    const settingsPayload = Object.fromEntries(
      settingsKeys.filter((key: string) => key in data).map((key: string) => [key, data[key]]),
    );
    const combinedValues = { ...settingsPayload, ...pagePayload };
    await Promise.all([
      updateDocument({
        documentId: pptxDocument.ID,
        projectId: projectData.ID,
        data: { values: combinedValues },
      }).unwrap(),
      updateProject({
        projectId: projectData.ID,
        data: { values: combinedValues },
      }).unwrap(),
    ]);
  };

  const handleSave = async (data?: any) => {
    const saveData = data ?? placematFormMethods.getValues();
    try {
      await saveValues(saveData);
      placematFormMethods.reset(saveData);
      setLongPullItemId(null);
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Failed to save placemat.' }));
      captureError(new Error('Failed to save placemat.'), { component: 'PlacematGeneratorPage', skipConsole: true });
    }
  };

  const handleBeforeGenerate = async () => {
    const data = placematFormMethods.getValues();
    try {
      await saveValues(data);
      placematFormMethods.reset(data);
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Failed to save placemat before generating.' }));
      captureError(new Error('Failed to save placemat before generating.'), {
        component: 'PlacematGeneratorPage',
        skipConsole: true,
      });
      throw new Error('pre-generate save failed');
    }
  };

  const handleTabSwitch = async (tab: string) => {
    setActiveSlot(null);
    await handleTabChange(tab);
  };

  const isPageSubmitDisabled = !pageReadiness[activeTab] || !activeTabIsDirty;
  const isSettingsSubmitDisabled = !isSettingsReady || !activeTabIsDirty;

  useDeepCompareEffect(() => {
    if (initialValues) {
      placematFormMethods.reset(initialValues);
    }
  }, [initialValues]);

  return (
    <PlacematContext.Provider
      value={{
        activeSlot,
        setActiveSlot,
        slotFieldName,
      }}
    >
      <FormProvider {...placematFormMethods}>
        <StyledLayout $leftExpanded={isLeftExpanded}>
          <StickyHeader top={STICKY_TOP} noWrapper disableStickyBelow={1280}>
            <StyledCardWrapper>
              <NgCard>
                <NgCardContent>
                  <StyledLeftColumn>
                    <StyledPageButton
                      $active={activeTab === SETTINGS_TAB}
                      type="button"
                      onClick={() => handleTabSwitch(SETTINGS_TAB)}
                    >
                      <StyledStatusDot $ready={isSettingsReady}>
                        <SlideStatusDot filled={isSettingsReady} />
                      </StyledStatusDot>
                      Settings
                    </StyledPageButton>

                    <NgDivider spacing="small" minHeight={10} />

                    <PlacematLayoutPreview
                      xLayout={xLayout}
                      thumbnailUrl={slideThumbnailBlobUrl}
                      activeSlot={activeTab !== SETTINGS_TAB ? activeSlot : null}
                      onSlotClick={activeTab !== SETTINGS_TAB ? setActiveSlot : undefined}
                      placeholderMessage={activeTab === SETTINGS_TAB ? 'Please select a page to see layout' : undefined}
                    />

                    {PAGES.map((page) => (
                      <StyledPageButton
                        key={page.name}
                        $active={activeTab === page.name}
                        type="button"
                        onClick={() => handleTabSwitch(page.name)}
                      >
                        <StyledStatusDot $ready={pageReadiness[page.name]}>
                          <SlideStatusDot filled={pageReadiness[page.name]} />
                        </StyledStatusDot>
                        {page.displayName}
                      </StyledPageButton>
                    ))}
                  </StyledLeftColumn>
                </NgCardContent>
              </NgCard>
              <NgPanelExpandButton expanded={isLeftExpanded} onClick={handleLeftRailToggleExpand} />
            </StyledCardWrapper>
          </StickyHeader>

          <NgCard>
            <NgCardContent>
              {activeTab === SETTINGS_TAB ? (
                <SchemaForm
                  key="settings"
                  schema={settingsSchema}
                  initialValues={{}}
                  isLoading={isLoading}
                  onSubmit={handleSave}
                  formMethods={placematFormMethods}
                  submitDisabled={isSettingsSubmitDisabled}
                  onSubmitButtonClick={() => handleSave()}
                  onDirtyChange={onDirtyChange}
                  onValidChange={onValidChange}
                />
              ) : (
                <SchemaForm
                  key={activeTab}
                  schema={activePageSchema}
                  initialValues={{}}
                  isLoading={isLoading}
                  onSubmit={handleSave}
                  prefix={activeTab + '.'}
                  formMethods={placematFormMethods}
                  submitDisabled={isPageSubmitDisabled}
                  onSubmitButtonClick={() => handleSave()}
                  onDirtyChange={onDirtyChange}
                  onValidChange={onValidChange}
                  noTargetMessage={activeSlot ? null : noTargetMessage}
                />
              )}
            </NgCardContent>
          </NgCard>

          <StickyHeader top={STICKY_TOP} noWrapper disableStickyBelow={1280}>
            <FormProvider {...generateFormMethods}>
              <GenerateDocumentsSection
                projectData={projectData}
                workspaceTemplateData={workspaceTemplateData}
                isQueryValid={isDocumentReady}
                isQueriesEnabled={false}
                onBeforeGenerate={handleBeforeGenerate}
                isFormDirty={placematFormMethods.formState.isDirty}
              />
            </FormProvider>
          </StickyHeader>
        </StyledLayout>
      </FormProvider>
    </PlacematContext.Provider>
  );
};
